import { useState, useEffect } from "react";
import TopBar from "../components/TopBar";
import {
  colors,
  fonts,
  fontSizes,
  headingStyle,
  bodyTextStyle,
} from "../theme";

export default function Playground() {
  const [isWide, setIsWide] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => setIsWide(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={styles.page}>
      <TopBar />
      <div style={styles.shell}>
        {/* Page Title */}
        <h1 style={styles.pageTitle}>Playground</h1>

        {/* Intro Section */}
        <div style={styles.intro}>
          <p style={styles.introParagraph}>
            This is where I experiment, build, and explore ideas that don't fit neatly into a job description. Some projects start as curiosities and evolve into tools I use daily. Others are experiments in learning new frameworks or solving personal problems with code.
          </p>
          <p style={styles.introParagraph}>
            The common thread: each one taught me something valuable, whether that's a new technical skill, a better way to think about product design, or just the satisfaction of shipping something functional.
          </p>
          <p style={styles.introParagraphLast}>
            Below are three areas where I've invested time building, iterating, and learning.
          </p>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Framing Sentence */}
        <p style={styles.framingSentence}>
          These are three lenses that shape how I think and build.
        </p>

        {/* Horizontal Bucket Header Row */}
        <div style={isWide ? styles.bucketHeaderWide : styles.bucketHeader}>
          <div style={styles.bucketLabel}>Fitness App</div>
          <div style={styles.bucketLabel}>Betting For Value</div>
          <div style={styles.bucketLabel}>Personal Optimization</div>
        </div>

        {/* Vertical Content Stack */}
        <div style={styles.contentStack}>
          {/* Fitness App Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitleFirst}>Fitness App</h2>
            <p style={styles.sectionParagraph}>
              I built a custom workout tracking app to solve a simple problem: existing fitness apps were either too complicated or didn't track the metrics I cared about. I wanted something minimal, fast, and tailored to progressive overload training.
            </p>
            <p style={styles.sectionParagraph}>
              The app tracks sets, reps, weight, and rest times across workouts. It calculates volume load automatically and surfaces trends over time so I can see where I'm progressing and where I'm plateauing.
            </p>
            <p style={styles.sectionParagraphLast}>
              Building this taught me about state management in React, local data persistence, and the importance of designing for mobile-first workflows. It's become a daily tool I rely on.
            </p>
          </section>

          {/* Betting For Value Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Betting For Value</h2>
            <p style={styles.sectionParagraph}>
              This project explores expected value in sports betting markets. I built a system that scrapes odds from multiple sportsbooks, identifies discrepancies, and calculates where the market might be mispricing outcomes.
            </p>
            <p style={styles.sectionParagraph}>
              The technical challenge was building a reliable data pipeline that could pull odds in real-time, normalize them across different formats, and run statistical models to surface potential value bets before lines moved.
            </p>
            <p style={styles.sectionParagraphLast}>
              Beyond the code, this project sharpened my understanding of probability, variance, and the discipline required to make data-driven decisions in noisy environments.
            </p>
          </section>

          {/* Personal Optimization Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Personal Optimization</h2>
            <p style={styles.sectionParagraph}>
              This is a collection of small tools and scripts I've built to automate repetitive tasks in my life—things like budgeting, habit tracking, and decision logging. Each one solves a specific friction point.
            </p>
            <p style={styles.sectionParagraph}>
              Some are simple Python scripts that run daily. Others are spreadsheet-driven systems with custom formulas and automation layers. The goal is always the same: reduce cognitive overhead and make better use of my time.
            </p>
            <p style={styles.sectionParagraphLast}>
              These projects remind me that the best software often solves boring problems elegantly. They're not flashy, but they compound value over time.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bgPrimary,
    color: colors.textPrimary,
  },

  shell: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "48px 20px 64px",
    fontFamily: fonts.body,
  },

  pageTitle: {
    ...headingStyle,
    fontSize: fontSizes.h1,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    margin: "0 0 32px 0",
  },

  intro: {
    marginBottom: 48,
    maxWidth: 800,
  },

  introParagraph: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    lineHeight: 1.7,
    margin: "0 0 20px 0",
    opacity: 0.9,
  },

  introParagraphLast: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    lineHeight: 1.7,
    margin: 0,
    opacity: 0.9,
  },

  divider: {
    height: 1,
    background: colors.border,
    marginBottom: 40,
    opacity: 0.4,
  },

  framingSentence: {
    ...bodyTextStyle,
    fontSize: fontSizes.small,
    lineHeight: 1.7,
    margin: "0 0 24px 0",
    opacity: 0.88,
  },

  bucketHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginBottom: 72,
    paddingBottom: 0,
  },

  bucketHeaderWide: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 64,
    marginBottom: 72,
    paddingBottom: 0,
  },

  bucketLabel: {
    fontFamily: fonts.heading,
    fontSize: fontSizes.body,
    fontWeight: 500,
    color: colors.textPrimary,
    opacity: 0.85,
    letterSpacing: "-0.01em",
  },

  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: 56,
  },

  section: {
    paddingTop: 0,
  },

  sectionTitle: {
    ...headingStyle,
    fontSize: fontSizes.h2,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    margin: "0 0 20px 0",
  },

  sectionTitleFirst: {
    ...headingStyle,
    fontSize: fontSizes.h2,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    margin: "0 0 20px 0",
    paddingTop: 8,
  },

  sectionParagraph: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    lineHeight: 1.7,
    margin: "0 0 20px 0",
    opacity: 0.88,
  },

  sectionParagraphLast: {
    ...bodyTextStyle,
    fontSize: fontSizes.body,
    lineHeight: 1.7,
    margin: 0,
    opacity: 0.88,
  },
};
